/**
 * @class
 * @function CountdownTimer
 * g4-mobile-speed-2026-08-03: update once per second and pause in hidden tabs.
 */
if (!customElements.get('countdown-timer')) {
  class CountdownTimer extends HTMLElement {
    constructor() {
      super();

      const timezone = this.dataset.timezone;
      const date = this.dataset.date.split('-');
      const day = Number.parseInt(date[0], 10);
      const month = Number.parseInt(date[1], 10);
      const year = Number.parseInt(date[2], 10);
      let targetHour = 0;
      let targetMinute = 0;

      if (this.dataset.time) {
        [targetHour, targetMinute] = this.dataset.time.split(':').map(Number);
      }

      const dateString = `${month}/${day}/${year} ${targetHour}:${targetMinute} GMT${timezone}`;
      this.countDownDate = new Date(dateString).getTime();

      if (this.dataset.durationDays) {
        this.totalDuration = Number.parseInt(this.dataset.durationDays, 10) * 24 * 60 * 60 * 1000;
      }

      const rolloverDays = Number.parseInt(this.dataset.rolloverDays, 10);
      this.rolloverDuration = Number.isFinite(rolloverDays) && rolloverDays > 0
        ? rolloverDays * 24 * 60 * 60 * 1000
        : 0;
      const rolloverUntil = Date.parse(this.dataset.rolloverUntil || '');
      this.rolloverUntil = Number.isFinite(rolloverUntil) ? rolloverUntil : 0;
      if (this.rolloverDuration > 0) {
        this.totalDuration = this.rolloverDuration;
      }

      this.timerId = null;
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
    }

    connectedCallback() {
      this.daysEl = this.querySelector('.days .countdown-timer--column--number');
      this.hoursEl = this.querySelector('.hours .countdown-timer--column--number');
      this.minutesEl = this.querySelector('.minutes .countdown-timer--column--number');
      this.secondsEl = this.querySelector('.seconds .countdown-timer--column--number');
      this.progressBarFill = this.closest('.countdown-banner')?.querySelector('.countdown-progress-bar--fill');

      if (!this.daysEl || !this.hoursEl || !this.minutesEl || !this.secondsEl) return;

      this.stopTimer();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      const shouldContinue = this.renderTime();
      if (shouldContinue && !document.hidden) this.scheduleNextTick();
    }

    disconnectedCallback() {
      this.stopTimer();
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    }

    handleVisibilityChange() {
      this.stopTimer();
      if (document.hidden || !this.isConnected) return;
      if (this.renderTime()) this.scheduleNextTick();
    }

    scheduleNextTick() {
      this.stopTimer();
      const delay = 1000 - (Date.now() % 1000) + 20;
      this.timerId = window.setTimeout(() => {
        this.timerId = null;
        if (!this.isConnected || document.hidden) return;
        if (this.renderTime()) this.scheduleNextTick();
      }, delay);
    }

    stopTimer() {
      if (this.timerId !== null) {
        window.clearTimeout(this.timerId);
        this.timerId = null;
      }
    }

    setText(element, value) {
      const nextValue = String(value);
      if (element.textContent !== nextValue) element.textContent = nextValue;
    }

    setProgress(width) {
      if (!this.progressBarFill) return;
      const nextWidth = `${width}%`;
      if (this.progressBarFill.style.width !== nextWidth) {
        this.progressBarFill.style.width = nextWidth;
      }
    }

    renderTime() {
      const now = Date.now();
      let distance = this.countDownDate - now;

      if (distance <= 0 && this.rolloverDuration > 0) {
        const cyclesElapsed = Math.floor((now - this.countDownDate) / this.rolloverDuration) + 1;
        const nextTarget = this.countDownDate + cyclesElapsed * this.rolloverDuration;
        this.countDownDate = this.rolloverUntil > 0
          ? Math.min(nextTarget, this.rolloverUntil)
          : nextTarget;
        distance = this.countDownDate - now;
      }

      if (distance <= 0) {
        this.setText(this.daysEl, '00');
        this.setText(this.hoursEl, '00');
        this.setText(this.minutesEl, '00');
        this.setText(this.secondsEl, '00');
        this.setProgress(0);

        if (this.rolloverUntil > 0 && now >= this.rolloverUntil) {
          const section = this.closest('.section-countdown-banner');
          if (section) section.hidden = true;
        }
        return false;
      }

      if (this.progressBarFill && this.totalDuration && this.totalDuration > 0) {
        const elapsed = this.totalDuration - distance;
        const progress = Math.max(0, Math.min(100, (elapsed / this.totalDuration) * 100));
        this.setProgress(progress.toFixed(4));
      }

      const days = Math.floor(distance / (1000 * 60 * 60 * 24));
      const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((distance % (1000 * 60)) / 1000);

      this.setText(this.daysEl, CountdownTimer.addZero(days));
      this.setText(this.hoursEl, CountdownTimer.addZero(hours));
      this.setText(this.minutesEl, CountdownTimer.addZero(minutes));
      this.setText(this.secondsEl, CountdownTimer.addZero(seconds));
      return true;
    }

    static addZero(value) {
      return value < 10 && value >= 0 ? `0${value}` : value;
    }
  }

  customElements.define('countdown-timer', CountdownTimer);
}
